// ─── Mental Health Lens Copy ─────────────────────────────────────────────────
//
// Lens-aware copy helpers for DashboardMentalHealth.tsx. Separates
// patient-friendly warm language from clinician-focused clinical framing
// without touching the underlying scoring engine or the PHQ-4 data shape.
//
// The crisis resource banner (PHQ-4 >= 9) is NOT surfaced here — its copy
// stays verbatim in the component because it's safety-critical and must
// not drift between lenses.
// ──────────────────────────────────────────────────────────────────────────────

import type { Lens } from './types';

// ─── Section heading ─────────────────────────────────────────────────────────

export function mentalHealthSectionLabel(lens: Lens): string {
  return lens === 'clinician' ? 'Mental Health Screening' : 'Wellbeing';
}

export function mentalHealthViewInsightsLabel(lens: Lens): string {
  return lens === 'clinician' ? 'Full grading →' : 'View insights →';
}

// ─── Mood & Anxiety Screen card labels ───────────────────────────────────────

export function moodScreenLabel(lens: Lens): string {
  return lens === 'clinician' ? 'PHQ-4 composite' : 'Mood & anxiety check-in';
}

export function primaryPatternLabel(lens: Lens): string {
  return lens === 'clinician' ? 'Primary phenotype' : 'Primary pattern';
}

// ─── Screening-level badge ───────────────────────────────────────────────────

export type ScreeningLevel = 'normal' | 'mild' | 'moderate' | 'severe';

interface BadgeCopy {
  text: string;
  /** Tailwind classes for bg/text/border. Stays constant across lens. */
  bg: string;
}

const PATIENT_BADGE: Record<ScreeningLevel, BadgeCopy> = {
  severe:   { text: 'Worth discussing with your clinician', bg: 'bg-rose-50 text-rose-700 border-rose-200' },
  moderate: { text: 'Some areas to monitor',                bg: 'bg-amber-50/70 text-amber-600 border-amber-100' },
  mild:     { text: 'A few things to watch',                bg: 'bg-yellow-50/60 text-yellow-600 border-yellow-100' },
  normal:   { text: 'Looking good',                         bg: 'bg-green-50 text-green-700 border-green-200' },
};

const CLINICIAN_BADGE: Record<ScreeningLevel, BadgeCopy> = {
  severe:   { text: 'Severe — review',        bg: 'bg-rose-50 text-rose-700 border-rose-200' },
  moderate: { text: 'Moderate — monitor',     bg: 'bg-amber-50/70 text-amber-700 border-amber-200' },
  mild:     { text: 'Mild — subclinical',     bg: 'bg-yellow-50/60 text-yellow-700 border-yellow-100' },
  normal:   { text: 'Within normal range',    bg: 'bg-green-50 text-green-700 border-green-200' },
};

export function screeningBadge(level: ScreeningLevel, lens: Lens): BadgeCopy {
  return (lens === 'clinician' ? CLINICIAN_BADGE : PATIENT_BADGE)[level];
}

// ─── Check-in nudge prompt (when weeks behind) ───────────────────────────────

export function checkinPrompt(weeksBehind: number, lens: Lens): string {
  if (lens === 'clinician') {
    return weeksBehind <= 2
      ? `Screening is ${weeksBehind} ${weeksBehind === 1 ? 'week' : 'weeks'} out of date — consider re-administering.`
      : `Screening is ${weeksBehind}+ weeks stale — re-administer before interpretation.`;
  }
  return weeksBehind <= 2
    ? 'Ready for a quick check-in? It helps us keep your picture current.'
    : "It's been a while — a quick update helps us support you better.";
}

export function checkinCtaLabel(lens: Lens): string {
  return lens === 'clinician' ? 'Administer →' : 'Update →';
}

// ─── No-data prompt ──────────────────────────────────────────────────────────

export function noDataPrompt(lens: Lens): string {
  return lens === 'clinician'
    ? 'No PHQ-4 screening on file. Administer an initial screening to populate this view.'
    : 'Complete your first check-in to see your wellbeing snapshot.';
}

export function noDataCtaLabel(lens: Lens): string {
  return lens === 'clinician' ? 'Start screening →' : 'Start check-in →';
}

// ─── Trend chart copy ────────────────────────────────────────────────────────

export function trendChartTitle(lens: Lens): string {
  return lens === 'clinician' ? 'PHQ-4 trajectory' : 'Wellbeing trend';
}

export function trendChartSubtitle(lens: Lens): string {
  return lens === 'clinician'
    ? 'Total screening score over time · lower is better'
    : 'Check-in score over time · lower is better';
}

export function trendTooltipLabels(lens: Lens): Record<string, string> {
  if (lens === 'clinician') {
    return {
      score: 'PHQ-4 total',
      depression: 'PHQ-2 depression',
      anxiety: 'GAD-2 anxiety',
    };
  }
  return {
    score: 'Total',
    depression: 'Low mood',
    anxiety: 'Worry',
  };
}

export function trendLegendLabels(lens: Lens): {
  normal: string;
  mild: string;
  moderate: string;
  severe: string;
} {
  if (lens === 'clinician') {
    return {
      normal: 'Normal (0–2)',
      mild: 'Mild (3–5)',
      moderate: 'Moderate (6–8)',
      severe: 'Severe (9–12)',
    };
  }
  return {
    normal: 'Looking good',
    mild: 'A few things',
    moderate: 'Monitor',
    severe: 'Discuss',
  };
}
